import { ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnChanges, PLATFORM_ID } from '@angular/core';
import { BaseChartComponent, calculateViewDimensions, ColorHelper, ScaleType, ViewDimensions } from '@swimlane/ngx-charts';
import { curveLinear } from 'd3-shape';
import { ScaleLinear, ScalePoint, scaleLinear, scalePoint } from 'd3-scale';
import { coloreClasse, RiskClass } from '../../../../../models/rischio-analitico.model';

export interface PuntoTrend {
  name: string;
  value: number;
  riskClass: RiskClass | null;
}

@Component({
  selector: 'ebw-monorepo-rischio-analitico-trend-chart',
  templateUrl: './rischio-analitico-trend-chart.component.html',
  styleUrls: ['./rischio-analitico-trend-chart.component.scss']
})
export class RischioAnaliticoTrendChartComponent extends BaseChartComponent implements OnChanges {
  @Input() series: PuntoTrend[] = [];
  @Input() yAxisLabel = '';

  curve = curveLinear;
  scaleType = ScaleType.Ordinal;
  // il bottom margin e' piu' alto del solito per lasciare spazio alle etichette dell'asse X ruotate
  // a 45 gradi (vedi .chart-wrapper in rischio-analitico-detail.component.scss)
  margin = [10, 20, 60, 60];
  xAxisHeight = 0;
  yAxisWidth = 0;

  dims: ViewDimensions;
  xScale: ScalePoint<string>;
  yScale: ScaleLinear<number, number>;
  colors: ColorHelper;
  transform: string;

  private readonly _lineaColorScheme = { name: 'trend', selectable: false, group: ScaleType.Ordinal, domain: ['#019bdb'] };

  constructor(chartElement: ElementRef, zone: NgZone, cd: ChangeDetectorRef, @Inject(PLATFORM_ID) platformId: object) {
    super(chartElement, zone, cd, platformId);
    this.animations = false;
  }

  get dataSerie(): { name: string; series: PuntoTrend[] } {
    return { name: this.yAxisLabel, series: this.series };
  }

  update(): void {
    super.update();
    if (!this.series?.length) return;

    this.dims = calculateViewDimensions({
      width: this.width,
      height: this.height,
      margins: this.margin,
      showXAxis: true,
      showYAxis: true,
      xAxisHeight: this.xAxisHeight,
      yAxisWidth: this.yAxisWidth,
      showYLabel: !!this.yAxisLabel
    });

    this.xScale = scalePoint()
      .range([0, this.dims.width])
      .padding(0.1)
      .domain(this.series.map((punto) => punto.name));

    const valori = this.series.map((punto) => punto.value).concat(0);
    this.yScale = scaleLinear()
      .range([this.dims.height, 0])
      .domain([Math.min(...valori), Math.max(...valori)]);

    this.colors = new ColorHelper(this._lineaColorScheme, ScaleType.Ordinal, [this.yAxisLabel]);
    this.transform = `translate(${this.dims.xOffset}, ${this.margin[0]})`;
  }

  updateXAxisHeight(event: { height: number }): void {
    this.xAxisHeight = event.height;
    this.update();
  }

  updateYAxisWidth(event: { width: number }): void {
    this.yAxisWidth = event.width;
    this.update();
  }

  coloreOsservazione(riskClass: RiskClass | null): string {
    return coloreClasse(riskClass, 'last');
  }

  titoloOsservazione(punto: PuntoTrend): string {
    return `${punto.name} — ${punto.value} ${this.yAxisLabel} (${punto.riskClass || 'classe non disponibile'})`;
  }
}
